import React, { useState, useEffect, useMemo } from 'react'
import CircularProgress from '@/components/CircularProgress'
import { getClients } from '@/api/sig-data'
import { transformClientResult, formatCurrency } from '@/lib/services/sig-data.service'
import { useAuth } from '@/hooks/useAuth'
import { useNavigate } from '@tanstack/react-router'
import { Building2, Users, Globe, Shield, Truck, Settings } from 'lucide-react'
import { ProtectedAttribute } from '@/components/protected-attribute'
import { ATTRIBUTES } from '@/lib/rbac/constants'
import type { ClientResult as ClientResultType, ClientResultApiResponse } from '@/types/financial'

export function ClientResult() {
  const { user: authUser } = useAuth();
  const navigate = useNavigate();
  const [clientData, setClientData] = useState<ClientResultApiResponse[]>([]);
  const [rawClientData, setRawClientData] = useState<ClientResultApiResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load client results data asynchronously
  useEffect(() => {
    const loadClientData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Fetch client data from API
        const currentYear = new Date().getFullYear().toString()
        const apiResults = await getClients(currentYear, { limit: 50 })

        // Store raw data for admin view
        setRawClientData(apiResults);

        // Use priority clients for card view
        const priorityClients = apiResults.filter((result: ClientResultApiResponse) => result.priority)
        setClientData(priorityClients);
      } catch (err) {
        console.error('Error loading client results:', err);
        setError('Falha ao carregar resultados dos clientes');
        setClientData([]);
        setRawClientData([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadClientData();
  }, []);

  // Calculate performance percentage (receitas / proa * 100)
  const calculatePerformance = (receitas: number, proa: number): number => {
    if (proa === 0) return 0
    return Math.min(Math.round((receitas / proa) * 100), 100)
  }

  // 💼 PERSONALIZED CLIENT RESULTS - Real data from API with actual logos
  const clientResults = useMemo(() => {
    return clientData.map(result => ({
      ...result,
      performance: calculatePerformance(result.receitas, result.proa),
      // Map fallback icons to components for clients without logos
      fallbackIconComponent: Building2 // Default fallback icon for all
    }));
  }, [clientData]);

  // Handle client card click navigation (will be implemented when route exists)
  const handleClientClick = (clientCode: string) => {
    // TODO: Navigate to client dashboard when route is created in Story 2.4/Epic 3
    console.log(`Navigate to client dashboard: ${clientCode}`)
    // navigate({ to: '/clients/$clientCode/dashboard', params: { clientCode } })
  }

  // Handle settings icon click (will open dashboard with settings section)
  const handleSettingsClick = (e: React.MouseEvent, clientCode: string) => {
    e.stopPropagation() // Prevent card click event
    // TODO: Navigate with settings section when route exists
    console.log(`Navigate to settings for client: ${clientCode}`)
    // navigate({
    //   to: '/clients/$clientCode/dashboard',
    //   params: { clientCode },
    //   search: { section: 'settings' }
    // })
  }

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent, clientCode: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleClientClick(clientCode)
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-8 animate-fade-in-up">
        <div className="card">
          <h3 className="font-display text-2xl font-bold text-white mb-8 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">
            📊 sss
          </h3>
          <div className="flex justify-center items-center min-h-[300px]">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-brand-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-brand-gray-light font-body">Carregando resultados dos clientes...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-8 animate-fade-in-up">
        <div className="card">
          <h3 className="font-display text-2xl font-bold text-white mb-8 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">
            📊 Resultado dos Clientes
          </h3>
          <div className="flex justify-center items-center min-h-[300px]">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-red-500 text-2xl">⚠️</span>
              </div>
              <p className="text-red-400 font-body mb-2">Erro ao carregar dados</p>
              <p className="text-brand-gray-light font-body text-sm">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in-up">
      {/* Client Results - Real data from RECEITAS JULHO 2025.xlsx with actual client logos
           PARCERIAS data is restricted to admin users only */}
      <div className="card">
        <h3 className="font-display text-2xl font-bold text-white mb-8 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">
          📊 Resultado dos Clientes
        </h3>
        {clientResults.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {clientResults.map((client) => {
            const FallbackIcon = client.fallbackIconComponent
            const isHighPerformance = client.performance >= 80
            const clientCode = client.clientCode

            return (
              <div
                key={clientCode}
                className={`card-interactive group p-6 transition-all duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 focus:ring-offset-brand-dark hover:scale-105 ${isHighPerformance ? 'border-success/30 bg-success/5' : ''}`}
                onClick={() => handleClientClick(clientCode)}
                onKeyDown={(e) => handleKeyDown(e, clientCode)}
                role="button"
                tabIndex={0}
                aria-label={`View dashboard for ${client.clientName}`}
              >
                {/* Settings Icon - Director Only */}
                <ProtectedAttribute
                  attributeName={ATTRIBUTES.CLIENT_FINANCIAL_DATA}
                  action="edit"
                  fallback={null}
                >
                  <button
                    onClick={(e) => handleSettingsClick(e, clientCode)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        e.stopPropagation()
                        // Convert keyboard event to mouse event for handler
                        handleSettingsClick(e as unknown as React.MouseEvent, clientCode)
                      }
                    }}
                    className="absolute top-4 right-4 p-2 rounded-monumenta bg-brand-dark/50 hover:bg-brand-primary/20 transition-colors duration-200 opacity-0 group-hover:opacity-100 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    aria-label={`Open settings for ${client.clientName}`}
                    tabIndex={0}
                  >
                    <Settings className="w-4 h-4 text-brand-primary" />
                  </button>
                </ProtectedAttribute>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-monumenta bg-brand-primary/10 group-hover:bg-brand-primary/20 transition-colors duration-200">
                      {/* <FallbackIcon className="w-6 h-6 text-brand-primary group-hover:scale-110 transition-transform duration-200" /> */}
                      {/* logo: 'https://img.logo.dev/www.monumenta.com.br?token=pk_U8n8doqgQHWzmrb30ROO6Q', */}
                      <img src={`https://img.logo.dev/www.${client.clientName}.com.br?token=pk_U8n8doqgQHWzmrb30ROO6Q`} alt={client.clientName} className="w-6 h-6" />

                    </div>
                    <h3 className="font-display text-lg font-bold text-white group-hover:text-brand-primary transition-colors duration-200">{client.clientName}</h3>
                  </div>
                  <div className="group-hover:scale-105 transition-transform duration-200">
                    <CircularProgress percentage={Math.floor(client.performance)} />
                  </div>
                </div>
                <ProtectedAttribute
                  attributeName={ATTRIBUTES.CLIENT_FINANCIAL_DATA}
                  action="view"
                  fallback={<></>}
                >
                  <div className="space-y-4">

                    <div className="flex justify-between items-center p-3 bg-brand-dark/30 rounded-monumenta">
                      <span className="text-sm text-brand-gray-light font-body">Realizado</span>
                      <span className="text-sm text-white font-display font-bold">
                        {formatCurrency(client.receitas)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-brand-dark/30 rounded-monumenta">
                      <span className="text-sm text-brand-gray-light font-body">PROA</span>
                      <span className="text-sm text-brand-gray-light font-display font-semibold">
                        {formatCurrency(client.proa)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-brand-dark/30 rounded-monumenta">
                      <span className="text-sm text-brand-gray-light font-body">Folha</span>
                      <span className="text-sm text-brand-gray-light font-display font-semibold">
                        {formatCurrency(client.folhaComum)}
                      </span>
                    </div>

                    <div className="flex justify-between items-center p-3 bg-brand-dark/30 rounded-monumenta">
                      <span className="text-sm text-brand-gray-light font-body">Custos Diretos</span>
                      <span className="text-sm text-brand-gray-light font-display font-semibold">
                        {formatCurrency(client.custos)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-brand-dark/30 rounded-monumenta">
                      <span className="text-sm text-brand-gray-light font-body">Custos Administrativos</span>
                      <span className="text-sm text-brand-gray-light font-display font-semibold">
                        {formatCurrency(client.despesas)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-brand-dark/30 rounded-monumenta">
                      <span className="text-sm text-brand-gray-light font-body">Impostos</span>
                      <span className="text-sm text-brand-gray-light font-display font-semibold">
                        {formatCurrency(client.impostos)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-brand-dark/30 rounded-monumenta">
                      <span className="text-sm text-brand-gray-light font-body">Ebitda</span>
                      <span className="text-sm text-brand-gray-light font-display font-semibold">
                        {formatCurrency(client.impostos)}
                      </span>
                    </div>


                  </div>
                </ProtectedAttribute>
              </div>
            )
          })}
          </div>
        ) : (
          <div className="flex justify-center items-center min-h-[200px]">
            <p className="text-brand-gray-light font-body">Nenhum resultado de cliente disponível</p>
          </div>
        )}
      </div>

      {/* Admin Only: Detailed Client Data Table */}
      {authUser?.role === 'admin' && rawClientData.length > 0 && (
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-display text-2xl font-bold text-white bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">
              🔧 Dados Detalhados (Admin)
            </h3>
            <div className="px-3 py-1 bg-brand-primary/20 rounded-monumenta">
              <span className="text-xs text-brand-primary font-semibold">ADMIN VIEW</span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-brand-gray/20">
                  <th className="text-left p-3 text-brand-gray-light font-body font-semibold sticky left-0 bg-brand-dark">Cliente</th>
                  <th className="text-right p-3 text-brand-gray-light font-body font-semibold">Código</th>
                  <th className="text-right p-3 text-brand-gray-light font-body font-semibold">Faturamento</th>
                  <th className="text-right p-3 text-brand-gray-light font-body font-semibold">Receitas</th>
                  <th className="text-right p-3 text-brand-gray-light font-body font-semibold">Repasse</th>
                  <th className="text-right p-3 text-brand-gray-light font-body font-semibold">Impostos</th>
                  <th className="text-right p-3 text-brand-gray-light font-body font-semibold">Custos</th>
                  <th className="text-right p-3 text-brand-gray-light font-body font-semibold">Folha Comum</th>
                  <th className="text-right p-3 text-brand-gray-light font-body font-semibold">Equipe Fixa</th>
                  <th className="text-right p-3 text-brand-gray-light font-body font-semibold">Empresa Parceira</th>
                  <th className="text-right p-3 text-brand-gray-light font-body font-semibold">Freela</th>
                  <th className="text-right p-3 text-brand-gray-light font-body font-semibold">Timesheet</th>
                  <th className="text-right p-3 text-brand-gray-light font-body font-semibold">Despesas</th>
                  <th className="text-right p-3 text-brand-gray-light font-body font-semibold">Margem Contrib</th>
                  <th className="text-right p-3 text-brand-gray-light font-body font-semibold">Res. Operac</th>
                  <th className="text-right p-3 text-brand-gray-light font-body font-semibold">Resultado</th>
                  <th className="text-right p-3 text-brand-gray-light font-body font-semibold">PROA</th>
                  <th className="text-right p-3 text-brand-gray-light font-body font-semibold">PROA Variance</th>
                  <th className="text-right p-3 text-brand-gray-light font-body font-semibold">PROA Var %</th>
                  <th className="text-left p-3 text-brand-gray-light font-body font-semibold">Data Source</th>
                  <th className="text-left p-3 text-brand-gray-light font-body font-semibold">Synced At</th>
                  <th className="text-center p-3 text-brand-gray-light font-body font-semibold">Priority</th>
                </tr>
              </thead>
              <tbody>
                {rawClientData.map((client, index) => {
                  const isPositive = client.resultado > 0
                  return (
                    <tr key={`${client.clientCode}-${index}`} className="border-b border-brand-gray/10 hover:bg-brand-dark/20 transition-colors">
                      <td className="p-3 text-white font-body font-semibold sticky left-0 bg-brand-dark">{client.clientName}</td>
                      <td className="p-3 text-brand-gray-light font-mono text-right">{client.clientCode}</td>
                      <td className="p-3 text-white font-mono text-right">{formatCurrency(client.faturamento)}</td>
                      <td className="p-3 text-white font-mono text-right">{formatCurrency(client.receitas)}</td>
                      <td className="p-3 text-brand-gray-light font-mono text-right">{formatCurrency(client.repasse)}</td>
                      <td className="p-3 text-red-400 font-mono text-right">{formatCurrency(client.impostos)}</td>
                      <td className="p-3 text-red-400 font-mono text-right">{formatCurrency(client.custos)}</td>
                      <td className="p-3 text-red-400 font-mono text-right">{formatCurrency(client.folhaComum)}</td>
                      <td className="p-3 text-red-400 font-mono text-right">{formatCurrency(client.equipeFixa)}</td>
                      <td className="p-3 text-red-400 font-mono text-right">{formatCurrency(client.empresaParceira)}</td>
                      <td className="p-3 text-red-400 font-mono text-right">{formatCurrency(client.freela)}</td>
                      <td className="p-3 text-brand-gray-light font-mono text-right">{formatCurrency(client.timesheet)}</td>
                      <td className="p-3 text-red-400 font-mono text-right">{formatCurrency(client.despesas)}</td>
                      <td className="p-3 text-white font-mono text-right">{formatCurrency(client.margemContrib)}</td>
                      <td className="p-3 text-white font-mono text-right">{formatCurrency(client.resOperac)}</td>
                      <td className={`p-3 font-mono text-right font-bold ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
                        {formatCurrency(client.resultado)}
                      </td>
                      <td className="p-3 text-brand-primary font-mono text-right">{formatCurrency(client.proa)}</td>
                      <td className={`p-3 font-mono text-right ${(client.proaVariance ?? 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {formatCurrency(client.proaVariance ?? 0)}
                      </td>
                      <td className={`p-3 font-mono text-right ${(client.proaVariancePercent ?? 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {client.proaVariancePercent?.toFixed(1) ?? '0.0'}%
                      </td>
                      <td className="p-3 text-brand-gray-light font-mono text-xs">{client.dataSource}</td>
                      <td className="p-3 text-brand-gray-light font-mono text-xs">
                        {new Date(client.syncedAt).toLocaleString('pt-BR')}
                      </td>
                      <td className="p-3 text-center">
                        {client.priority && (
                          <span className="inline-block w-2 h-2 bg-brand-primary rounded-full"></span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-4 p-4 bg-brand-dark/30 rounded-monumenta">
            <p className="text-xs text-brand-gray-light font-body">
              <strong className="text-white">Legenda:</strong> Valores em vermelho = custos/despesas |
              Valores em verde/vermelho (Resultado e Variance) = lucro/prejuízo |
              PROA Variance = Resultado - PROA |
              Ano: {rawClientData[0]?.year || 'N/A'}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
